import { generalizeContentType } from '@megogo/et';
import { scopeLogger } from '../scope-logger.js';
/**
 * @namespace ET
 */
/**
 * @typedef {Object} ETInterface
 *
 * @property {(target: ET.Target) => void} startSession
 * @property {() => void} closeSession
 * @property {(event: *, context: *) => void} track
 */

/**
 * @typedef {Object} ET.Feed
 *
 * @property {string} type
 * @property {string} algorithm
 * @property {number} id based on code?
 * @property {string} code
 * @property {string} [title='']
 * @property {[{id: number, type: "landing", position: number}]}  objects_extended
 */

/**
 * @typedef {Object} ET.UI
 *
 * @property {"image"|"button"|"close"} element_type
 * @property {string} element_code
 * @property {string} [element_text='']
 */

/**
 *
 * @typedef {Object} ET.Target
 *
 * @property {string} object_type "CAMPAIGN", // Тип объекта на интерактиве
 * @property {number} object_id hash(campaign_id), // хеш ID  кампании
 * @property {string} object_code campaign_id
 * @property {number} root_id 49025, // ID объекта который играет в плеере
 * @property {string} root_code "VOD" // тип объекта в плеере
 *
 */

/**
 * @typedef {Object} ET.TrackingEvent
 *
 * @property {string} event Client's event
 * @property {string} target ID of element having tracking event
 * @property {string} trackName
 * @property {{feed: ET.Feed, ui?: ET.UI}} payload
 *
 */

const console = scopeLogger('RMI:Promo:ET');
const mock = (event, payload) => {
	console.log('fake tracker', event, payload);
};

/**
 *
 * @param trackEvent
 * @return {ETInterface}
 * @constructor
 */
export class ET {
	constructor (tracker = mock) {
		this._tracker = tracker;

		/**
		 *
		 * @type {Map<string, {factory(origin: Object): Object, alias: string}>}
		 */
		this._trackingEvents = new Map([
			[ 'promo-click', {
				alias: 'ui_click',
				factory: this._trackClick.bind(this),
			} ],
			[ 'slide-view', {
				alias: 'impression',
				factory: this._trackImpression.bind(this),
			} ],
		]);

		/**
		 * Maps slide.id to slide feed data
		 * @type {Map<string, any>}
		 */
		this._feeds = new Map();
		this._enabled = false;

		/**
		 * @type {{object_id: number, object_code: string}}
		 */
		this._campaignTarget = {};
		/**
		 * @type {{root_id: number, root_code: string, virtual_object_id: number?}}
		 */
		this._videoTarget = {};
	}

	/**
	 * @return {Set<string>}
	 */
	get eventsList () {
		return new Set(Array.from(this._trackingEvents.keys()));
	}

	set enabled (value) {
		this._enabled = value;
	}

	set video (video) {
		const log = {};
		log.before = JSON.stringify(this._videoTarget, null, " ");

		this._videoTarget = {
			root_id: Number(video.object_id),
			root_code: generalizeContentType(video.content_type),
			object_title: '', // Sorry, no video title in i9e at all
		};

		if (video.channel_id) {
			this._videoTarget.virtual_object_id = video.video_id;
		}
		log.after = JSON.stringify(this._videoTarget, null, " ");
		console.log('Update target video', video);
		console.log(log);
	}

	set creative (creative) {
		this._campaignTarget = {
			object_id: creative.meta?.campaign?.id_hash ?? 0,
			object_code: creative.meta?.campaign?.id ?? '',
		};

		creative.slides.forEach((slide, index) => {
			const slideMode = slide._displayMode === 'standard' ? 'min' : 'max';
			const feed = {
				type: `promo_interactive_${slideMode}`,
				algorithm: 'rotator',
				id: creative.idHash,
				code: creative.id,
				title: creative.meta?.creative?.title ?? '', // Sorry, we did not have any poll title at client at all
				objects_extended: [ {
					id: slide._numberId,
					type: 'landing',
					position: index + 1,
				} ]
			};

			this._feeds.set(slide.id, feed);
		});
	}

	track (event) {
		if (!this._enabled) {
			return;
		}

		const tracking = this._trackingEvents.get(event.type);

		if (!tracking) {
			// TODO: error?
			return;
		}
		const payload = tracking.factory(event.detail.origin);

		this._tracker(tracking.alias, payload);
	}

	_trackImpression (origin) {
		const slide = origin.target;
		return this._trackEvent(slide);
	}

	_trackClick (origin) {
		const slide = origin.target.slide;

		const payload = {
			ui: {
				element_type: origin.target._type,
				element_code: origin.target.id,
				element_text: origin.caption,
			}
		};
		if (origin.target.role === 'anchor') {
			Object.assign(payload.ui, {
				element_code: origin.target.link,
				element_type: origin.target.linkType,
			});
		}
		if (origin.target.role === 'closeButton') {
			payload.ui.element_code = origin.action === 'click' ? 'close' : 'auto_close';
		}

		return this._trackEvent(slide, payload);
	}

	_trackEvent (slide, payload) {
		return Object.assign({
			target: this._target,
			feed: this._feeds.get(slide.id),
		}, payload);
	}

	/**
	 *
	 * @return {ET.Target}
	 */
	get _target () {
		return Object.assign(
			{ object_type: "CAMPAIGN" },
			this._campaignTarget,
			this._videoTarget,
		);
	}
}
